import { ApiError } from './api.js';

/**
 * סירוב של השרת, בשפה של העובד.
 *
 * 15/09/2026, בדיקת עבודה במקביל: כל סירוב בעמדה הוצג כ-`userMessage` של השרת —
 * בעברית. עובד שמסך העמדה שלו ברוסית קרא עברית בדיוק ברגע שמשהו עצר אותו
 * („החלק ממתין לבדיקת הנדסה…", „יש להשלים לפני תחילת העבודה…").
 *
 * ── הכלל ──
 * · בעברית — ההודעה של השרת כמו שהיא: היא עברית, והיא המדויקת ביותר.
 * · בשפה אחרת — לפי קוד השגיאה, והנתונים (שם תחנה, סעיפי צ׳קליסט, מכונה) מתוך
 *   `details` ומוכנסים כמו שהם: הם נתונים, לא ממשק.
 * · קוד שאין לו תרגום — ההודעה של השרת. לעולם לא ריק.
 *
 * השפה נקראת מ-`<html lang>`, שספק ה-i18n כותב בכל החלפה — כדי שהפונקציה תעבוד
 * מכל רכיב בלי להעביר את השפה דרך כל שכבה.
 */
type Translate = (key: string, vars?: Record<string, string | number>) => string;

const KEY: Record<string, string> = {
  PROJECT_CANCELLED: 'err.projectCancelled',
  // ביטול חלק (U-0924-07). הסיבה ב-`details.cancelReason` — לא `reason`, שהוא
  // מפתח-המשנה של הפונקציה הזאת — ונאמרת כמו שהמהנדס כתב.
  PART_CANCELLED: 'err.partCancelled',
  ECO_OPEN: 'err.ecoOpen',
  // FAI של גרסת קטלוג (C2-F15) — יחידה שנייה לא מתחילה עד החתימה.
  FAI_PENDING: 'err.faiPending',
  // אלי, 24/09/2026: „עצירת ייצור חוסמת התחלת עבודה בכל מקום". הסיבה נוסעת
  // ב-`details.text` — היא מה שהמנהל כתב, ונאמרת כמו שהיא.
  PRODUCTION_HOLD: 'err.productionHold',
  REVISION_NOT_CHANGED: 'err.revisionNotChanged',
  NC_BLOCKING: 'err.ncBlocking',
  CHECKLIST_INCOMPLETE: 'err.checklistIncomplete',
  ENG_INSPECTION_PENDING: 'err.engInspectionPending',
  STAGE_COMPLETE: 'err.stageComplete',
  OLD_VERSION: 'err.oldVersion',
  APPROVAL_PENDING: 'err.approvalPending',
  // ⚠ 27/09/2026 — ASSEMBLY כבר אינו נזרק על **התחלה** (אלי: „לאפשר חופשי, עם
  // סימון „חלקים חסרים""), והמפתח נשאר מפני שבקשות pending שכבר בייצור יחזירו
  // אותו עד שיפוגו. הסירוב החי הוא עכשיו על **סיום** השלב, למטה.
  'APPROVAL_REQUIRED:ASSEMBLY': 'err.approvalAssembly',
  'APPROVAL_REQUIRED:SKIP': 'err.approvalSkip',
  // השמות נוסעים ב-`details.missingLabel` כמחרוזת מוכנה — מערך של אובייקטים
  // נושר בלולאה שלמטה, ואז העובד הרוסי היה מקבל „חסרים חלקים:" ואחריו כלום.
  ASSEMBLY_CHILDREN_MISSING: 'err.assemblyChildrenMissing',
  APPROVAL_INVALID: 'st.badManagerCode',
  NOT_SESSION_OWNER: 'err.notSessionOwner',
  PART_NOT_FOUND: 'err.partNotFound',
  // נחתך בלייזר ואין לו שרטוט — מתי נחתך, ועל איזו הרכבה אפשר לעבוד בינתיים (19/10/2026).
  'PART_NOT_FOUND:CUT_NO_DRAWING_PARENT': 'err.partCutNoDrawingParent',
  'PART_NOT_FOUND:CUT_NO_DRAWING': 'err.partCutNoDrawing',
  DEPT_REQUIRED: 'err.deptRequired',
  DEPT_MISMATCH: 'err.deptMismatch',
  EMPLOYEE_INACTIVE: 'err.employeeInactive',
  SESSION_ALREADY_CLOSED: 'err.sessionClosedByManager',
  MACHINE_REQUIRED: 'err.machineRequired',
  MACHINE_NOT_ACTIVE: 'err.machineNotActive',
  MACHINE_WRONG_DEPARTMENT: 'err.machineWrongDept',
  MAINTENANCE_DUE: 'err.maintenanceDue',
  // ── 17/09/2026: הקיוסקים האחרים ─────────────────────────────────────────
  //
  // המנגנון הזה שירת מסך אחד. שמונת הקיוסקים האחרים הציגו `userMessage`
  // גלוי — עברית, בדיוק ברגע שמשהו עצר את העובד. הקודים כאן הם אלה
  // שנמדדו כנגישים מהקיוסקים.
  //
  // ── ורק קוד שנושא מסר אחד ───────────────────────────────────────────────
  //
  // `NOT_FOUND` נושא 27 מסרים שונים ו-`VALIDATION` 23. מיפוי שלהם היה
  // מחליף „מוט לא נמצא" ב„לא נמצא" — תרגום שמוחק את הסיבה גרוע מעברית
  // מדויקת, ולכן הם נשארים בכוונה ונופלים להודעת השרת.
  COWORKERS_DISABLED: 'err.coworkersDisabled',
  COWORKER_INVALID: 'err.coworkerInvalid',
  TOO_MANY_COWORKERS: 'err.tooManyCoworkers',
  'DRAWING_NOT_YOURS:NO_DEPARTMENT': 'err.noDepartment',
  DRAWING_NOT_YOURS: 'err.drawingNotYours',
  DRAWING_FORBIDDEN: 'err.drawingForbidden',
  BADGE_TOKEN_INVALID: 'err.badgeTokenInvalid',
  CARD_NOT_ENROLLED: 'err.cardNotEnrolled',
  EMPLOYEE_NOT_FOUND: 'err.employeeNotFound',
  NOT_YOURS: 'err.sessionNotYours',
  NOT_TASK_OWNER: 'err.taskNotYours',
  ALREADY_REPORTED: 'err.alreadyReported',
  SESSION_ALREADY_OPEN: 'err.sessionAlreadyOpen',
  INVALID_PAYLOAD: 'err.invalidPayload',
  LASER_EXCEL_ONLY: 'err.laserExcelOnly',
  LASER_NOT_CONFIGURED: 'err.laserNotConfigured',
  INVALID_FILE: 'err.invalidFile',
  NO_FILE: 'err.noPhoto',
  NO_PARTS: 'err.noParts',
  NO_REPORTABLE_PARTS: 'err.noReportableParts',
  ITEM_NOT_DELETABLE: 'err.itemNotDeletable',
  BAD_VEHICLE_CODE: 'err.badVehicleCode',
  TRIP_CLOSED: 'err.tripClosed',
  TRIP_INCOMPLETE: 'err.tripIncomplete',
  ODOMETER_BACKWARDS: 'err.odometerBackwards',
  ODOMETER_LOW: 'err.odometerLow',
  // קיוסק הרכב, סבב שני (02/11/2026) — קוד לכל משפט, ושומר ב-vehicle-kiosk-ru.test.ts.
  VEHICLE_FORM_INVALID: 'err.vehicleFormInvalid',
  TRIP_NOT_FOUND: 'err.tripNotFound',
  TRIP_OTHER_VEHICLE: 'err.tripOtherVehicle',
  TRIP_OTHER_DRIVER: 'err.tripOtherDriver',
  TRIP_PROJECT_REQUIRED: 'err.tripProjectRequired',
  REFUEL_INCOMPLETE: 'err.refuelIncomplete',
  CHECK_FUTURE_DAY: 'err.checkFutureDay',
  CHECK_NOTE_REQUIRED: 'err.checkNoteRequired',
  MONTH_ALREADY_REPORTED: 'err.monthAlreadyReported',
  MONTH_READING_LOW: 'err.monthReadingLow',
  NOT_MONTHLY_VEHICLE: 'err.notMonthlyVehicle',
  MONTH_CLOSED: 'err.monthClosed',
  MONTH_INCOMPLETE: 'err.monthIncomplete',
  UNAUTHENTICATED: 'err.mustSignIn',
  // „בוקר טוב" — סימון „נטען" בעמדה (אלי 12/10/2026, החלטה 159).
  FIELD_NOT_ON_CREW: 'err.fieldNotOnCrew',
  FIELD_JOB_CLOSED: 'err.fieldJobClosed',

  // ── 15/10/2026: סקר הקודים שעוד הגיעו לעובד הרוסי בעברית ─────────────────
  //
  // אלי: „עובדי הרצפה דוברי הרוסית רואים שגיאות בעברית". כל קוד כאן נושא מסר
  // אחד (או כמה משפטים שאומרים אותו דבר). נתון שהשרת מכניס למשפט אבל לא שולח
  // ב-`details` (מספר חלק, שם שלב) יורד בתרגום — החלק כבר על המסך מול העובד.
  // `DRAWING_MISSING` מסתעף על `details.decision` כמו ש-`reason` מסתעף.
  'DRAWING_MISSING:WAIT': 'err.drawingMissingWait',
  DRAWING_MISSING: 'err.drawingMissing',
  EXCEEDS_PREVIOUS_STAGE: 'err.exceedsPreviousStage',
  // כל מה שהגיע כבר דווח — בלי „עד 0" (מנכ"ל 03/11/2026).
  'EXCEEDS_PREVIOUS_STAGE:ALL_REPORTED': 'err.exceedsPreviousStageAll',
  // תג עם קוד (אלי 16/10/2026) — badge-key.ts בשרת.
  BADGE_REVOKED: 'err.badgeRevoked',
  BADGE_SCAN_ONLY: 'err.badgeScanOnly',
  BADGE_OLD: 'err.badgeOld',
  PHOTO_PICK_OFF: 'err.photoPickOff',
  HAS_DRAWING: 'err.hasDrawing',
  NOT_A_MANAGER: 'err.notAManager',
  INVALID_MINUTES: 'err.invalidMinutes',
  NOT_SELF_PRIOR: 'err.notSelfPrior',
  PART_DONE: 'err.partDone',
  PRIOR_NOT_EARLIER: 'err.priorNotEarlier',
  PRIOR_NO_OWN_STAGE: 'err.priorNoOwnStage',
  PRIOR_STAGE_MACHINE: 'err.priorStageMachine',
  ROUTE_CHANGED: 'err.routeChanged',
  ROUTE_NOT_SEQUENTIAL: 'err.routeNotSequential',
  SHARED_SCANNED_ROUTE: 'err.sharedScannedRoute',
  WELD_VIA_CHILD_INVALID: 'err.weldViaChildInvalid',
  PART_NOT_IN_WORK: 'err.partNotInWork',
  PROJECT_CLOSED: 'err.projectClosed',
  BADGE_REQUIRED: 'err.sawBadgeRequired',
  SAW_NOT_IN_DEPARTMENT: 'err.sawNotInDepartment',
  SAW_REQUIRED: 'err.sawRequired',
  REMAINING_UNANSWERED: 'err.remainingUnanswered',
  LASER_ALREADY_UPLOADED: 'err.laserAlreadyUploaded',
  NO_FILES: 'err.noFileChosen',
  APPROVAL_CODE_UNSET: 'err.approvalCodeUnset',
  APPROVAL_CODE_DEFAULT: 'err.approvalCodeDefault',
  CARD_REVOKED: 'err.cardRevoked',
  // דף הרכבה מודפס (02/11/2026).
  ASSEMBLY_SHEET_UNKNOWN: 'err.asmSheetUnknown',
  STATION_CLOCK_OPEN: 'err.asmStationClockOpen',
  ALREADY_ASSEMBLED: 'err.asmAlreadyAssembled',
  NOT_ASSEMBLY_DRAWING: 'err.asmNotAssemblyDrawing',
  ASSEMBLY_SESSION_ENDED: 'err.asmSessionEnded',
};

/**
 * ── אין רשת / השרת נפל — אומרים את זה, בשפה של העובד (סקר העמדה 11/10 #3) ──
 *
 * נמדד: 500 הציג „Internal server error" באנגלית, ורשת שנפלה בשדה החיפוש
 * הציגה „לא נמצא חלק. בדוק את המספר" — שקר שמוליך את העובד לבדוק מספר תקין.
 * `NETWORK` בסטטוס 0 = הבקשה לא הגיעה; 5xx (או `NETWORK` עם סטטוס) = השרת לא
 * ענה כמו שצריך. בשניהם המשפט אומר מה לעשות: לנסות שוב, ואם חוזר — מנהל.
 */
export function connectionErrorKey(e: unknown): 'err.offline' | 'err.serverDown' | null {
  if (e instanceof ApiError) {
    if (e.code === 'NETWORK' && e.status === 0) return 'err.offline';
    if (e.status >= 500 || e.code === 'INTERNAL' || e.code === 'NETWORK') return 'err.serverDown';
    return null;
  }
  // fetch שנזרק ישירות (לא דרך `api`) — TypeError „Failed to fetch".
  if (e instanceof TypeError) return 'err.offline';
  return null;
}

/** האם הטקסט קריא לעובד — יש בו אות עברית או קירילית (לא „Internal server error"). */
const readable = (s: string): boolean => /[֐-׿Ѐ-ӿ]/.test(s);

export function stationErrorText(e: unknown, t: Translate, fallbackKey = 'common.error', lang?: string): string {
  const conn = connectionErrorKey(e);
  if (conn) return t(conn);
  if (!(e instanceof ApiError)) return t(fallbackKey);
  // ── 5 שרטוטים במקביל (אלי 30/09) ─────────────────────────────────────────
  // בכל שפה, גם בעברית: ההודעה חייבת לומר **אילו** שרטוטים פתוחים, כדי
  // שהעובד ידע מה לסיים. `openSessions` הוא מערך אובייקטים ונושר בלולאה למטה.
  if (e.code === 'PARALLEL_LIMIT') {
    const open = (e.details as { openSessions?: { partNumber?: string }[] } | undefined)?.openSessions ?? [];
    const parts = open.map((o) => o.partNumber).filter((p): p is string => !!p).join(', ');
    return t('err.parallelLimit', { parts: parts || '—' });
  }
  const language = lang ?? (typeof document !== 'undefined' ? document.documentElement.lang : 'he');
  // טקסט גולמי באנגלית מהשרת (או מפרוקסי) לעולם לא מגיע לעובד.
  const server = e.userMessage && readable(e.userMessage) ? e.userMessage : '';
  if (!language || language === 'he') return server || t(fallbackKey);

  const d = (e.details && typeof e.details === 'object' ? e.details : {}) as Record<string, unknown>;
  const sub = typeof d.reason === 'string' ? d.reason : typeof d.decision === 'string' ? d.decision : undefined;
  const key = (sub ? KEY[`${e.code}:${sub}`] : undefined) ?? KEY[e.code];
  if (!key) return server || t(fallbackKey);

  const vars: Record<string, string | number> = {};
  for (const [k, v] of Object.entries(d)) {
    if (Array.isArray(v)) vars[k] = v.filter((x) => typeof x === 'string' || typeof x === 'number').join(' · ');
    else if (typeof v === 'string' || typeof v === 'number') vars[k] = v;
  }
  return t(key, vars);
}
